// imports
import { FC, useState, MouseEvent } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { Stack, Box } from '@mui/material';

// local imports
import ReusableMenuTypography from './ReusableMenuTypography/ReusableMenuTypography';
import { LINK_BLUE, TEXT_GRAY } from '../../../constants/colors';

// Define a custom styled Link that accepts the disabled prop
const ReusableMenuLink = styled(Link)<{ disabled?: boolean }>(
  ({ disabled }) => ({
    fontSize: '1rem',
    display: disabled ? 'none' : 'grid',
    color: disabled ? 'lightgray' : LINK_BLUE,
    pointerEvents: disabled ? 'none' : 'auto',
    textAlign: 'right',
  })
);

// Clickable menu
const ReusableMenuListItemButton = styled(ListItemButton)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  border: `1px solid #80858C`,
  opacity: 1,
  pointerEvents: 'auto',
}));

// Vertical bar in the menu
const MenuBarDiv = styled('div')<{ barMargin?: string }>(({ barMargin }) => ({
  borderLeft: '1px solid #EFF0F0',
  height: '20px',
  margin: barMargin || '0 0px',
}));

// Downward arrow in the menu
const ReusableMenuExpandMoreIcon = styled(ExpandMoreIcon)(() => ({
  color: TEXT_GRAY,
  fontSize: '30px',
  marginRight: '-15px',
}));

// For aligning the vertical bar with the downward arrow in the menu
const ReusableMenuStack = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  color: TEXT_GRAY,
  fontSize: '30px',
}));

// For the full menu component
const ReusableMenuBox = styled(Box)(() => ({
  marginBottom: '16px',
}));

// For the dropdown menu
const ReusableStyledMenu = styled(Menu)(() => ({
  zIndex: 2000,
  '& .MuiPaper-root': {
    maxHeight: '250px',
    overflowY: 'auto',
  },
}));

interface ReusableMenuProps {
  title: string;
  options: { key: string; value: string }[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  learnMoreLink?: string;
  disabled?: boolean;
  menuItemTestId?: (key: string) => string;
  barMargin?: string;
}

const ReusableMenu: FC<ReusableMenuProps> = ({
  title,
  options,
  selectedIndex,
  onSelect,
  learnMoreLink,
  disabled = false,
  menuItemTestId,
  barMargin,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const learnMore = () => {
    if (!learnMoreLink) return null;
    return (
      <ReusableMenuLink
        href={learnMoreLink}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled}
        aria-label={`About ${options[selectedIndex]?.value ?? title.toLowerCase()}`}
      >
        {`About this ${title.toLowerCase()}`}
      </ReusableMenuLink>
    );
  };

  return (
    <ReusableMenuBox>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <ReusableMenuTypography>{title}</ReusableMenuTypography>
        <Box sx={{ flexGrow: 1 }} />
        {learnMore()}
      </Stack>
      {/* Top margin in List allows for the full focus outline of the "Learn more" link to display */}
      <List
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid #80858C',
          paddingTop: 0,
          paddingBottom: 0,
          top: '2px',
        }}
      >
        <ListItem disablePadding>
          <ReusableMenuListItemButton
            dense
            aria-label={`${title}: ${options[selectedIndex]?.value}`}
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClickListItem}
            data-testid={`menu-${title.toLowerCase().replace(/\s+/g, '-')}`}
            sx={{
              pl: '8px',
            }}
          >
            <ListItemText
              secondary={options[selectedIndex]?.value}
              sx={{
                '& .MuiListItemText-secondary': {
                  fontSize: '1rem',
                  color: '#000',
                  lineHeight: '1rem',
                },
              }}
            />
            <ReusableMenuStack>
              <MenuBarDiv barMargin={barMargin} />
              <ReusableMenuExpandMoreIcon />
            </ReusableMenuStack>
          </ReusableMenuListItemButton>
        </ListItem>
      </List>
      <ReusableStyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            width: anchorEl ? anchorEl.clientWidth : 'auto',
          },
        }}
      >
        {options.map(({ key, value }, index) => {
          // For data-testid - normalize by converting to lowercase and replacing spaces with hyphens
          const normalizedKey = key.toLowerCase().replace(/\s+/g, '-');

          return (
            <MenuItem
              dense
              key={key}
              selected={index === selectedIndex}
              onClick={() => {
                if (!disabled) {
                  onSelect(index);
                }
                handleClose();
              }}
              data-testid={
                menuItemTestId
                  ? menuItemTestId(key)
                  : `menu-item-${normalizedKey}`
              }
              sx={{ textWrap: 'wrap', fontSize: '1rem' }}
            >
              {value}
            </MenuItem>
          );
        })}
      </ReusableStyledMenu>
    </ReusableMenuBox>
  );
};

export default ReusableMenu;
